// InstantSearch front-end for the "postes" index.
//
// This UI is the read side of the pipeline whose write side is the Rust
// scraper (`src/bin/scraper`). Every `hit.*` field read below maps directly to
// the serde (camelCase) serialization of `JobPosting` in
// `src/lib/domain/models/metadata.rs`:
//
//   hit.corps.libelleCourt / .libelle      <- Corps
//   hit.etablissementLibelle               <- etablissement_libelle
//   hit.dateOuvertureCandidatures          <- date_ouverture_candidatures
//   hit.dateFermetureCandidatures          <- date_fermeture_candidatures
//   hit.editionPoste                       <- edition_poste (public URL)
//   hit.region.libelle                     <- region
//   hit.sections[].{code,libelle}          <- sections
//   hit.domaineEuraxessComplets[].libelle  <- domaine_euraxess_complets
//   hit.laboratoires.laboratoiresReferences[].{numeroNational,sigleUnite,libelle}
//   hit.contactEtablissements[].{adresseElectronique,prenom,nomUsage}
//   hit.typeCandidatures[].libelle         <- type_candidatures
//   hit.descriptionPosteFrancais           <- description_poste_francais
//   hit.extracted_text                     <- extracted_text (NOT camelCased)
//
// The Meilisearch host below MUST point at the same instance the scraper
// uploads to via its MEILISEARCH_URL env var. The key here is a *search-only*
// key; the scraper uses an admin key for indexing.
import instantsearch from 'instantsearch.js';
import moment from 'moment';
import {
  configure,
  hits,
  pagination,
  panel,
  refinementList,
  searchBox,
  trendingItems,
  currentRefinements,
  stats,
  hitsPerPage
} from 'instantsearch.js/es/widgets';

import 'instantsearch.css/themes/satellite.css';

import { MeiliSearch } from "meilisearch";
import { instantMeiliSearch } from '@meilisearch/instant-meilisearch'

const { searchClient, setMeiliSearchParams } = instantMeiliSearch(
  'https://sciences.re/search', // Host (must match the scraper's MEILISEARCH_URL instance)
  '6e51d0cbb67b449315ef8468c68fac2733ceaba412f1fc81561f2617ce345b82' // Search-only API key
)
const search = instantsearch({
  indexName: 'postes',
  routing: true,
  searchClient,
  insights: false,
});

const laboratoires = function (hit) {
  try {
    const l = hit._highlightResult.laboratoires.laboratoiresReferences.map(el => `<ul><a target="_blank" href="https://appliweb.dgri.education.fr/rnsr/PresenteStruct.jsp?numNatStruct=${el.numeroNational.value}&PUBLIC=OK">▸ ${el.sigleUnite.value} − ${el.libelle.value}</a></ul>`);
    if (l.length != 0) {
      return `<p><b>Laboratoires : </b><li>${l.join("")}</li></p>`
    } else {
      return ``
    }
  } catch (error) {
    return ``;
  }
}

const sections = function (hit) {
  try {
    const l = hit._highlightResult.sections.map(el => `${el.code.value} - ${el.libelle.value}`)
    if (l.length != 0) {
      return `<p><b>Sections: </b><li>${l.join(", ")}</li></p>`
    } else {
      return ``
    }
  } catch (error) {
    return ``;
  }
}

const localisation = function (hit) {
  try {
    return `<p><b>Localisation :</b><li>${hit.region.libelle}</li></p>`
  } catch (error) {
    return ``;
  }
}


const domaines = function (hit) {
  try {
    const l = hit._highlightResult.domaineEuraxessComplets.map(el => `${el.libelle.value}`)
    if (l.length != 0) {
      return `<p><b>Domaines: </b><li>${l.join(", ")}</li></p>`
    } else {
      return ``
    }
  } catch (error) {
    return ``;
  }
}

const contacts = function (hit) {
  try {
    const l = hit.contactEtablissements.map(el => `<a class="button orange" href="mailto:${el.adresseElectronique}">${el.prenom} ${el.nomUsage}</a>`);
    if (l.length != 0) {
      return `<p><b>Contact Établissement :</b><li>${l.join(", ")}</li></p>`
    } else {
      return ``
    }
  } catch (error) {
    return ``;
  }
}

const typeCandidatures = function (hit) {
  try {
    const l = hit._highlightResult.typeCandidatures.map(el => `${el.libelle.value}`);
    if (l.length != 0) {
      return `<p><b>Types de candidatures :</b><li>${l.join(", ")}</li></p>`
    } else {
      return ``
    }
  } catch (error) {
    return ``;
  }
}

const description = function (hit) {
  try {
    return `<p><b>Description:</b><li>${hit._snippetResult.descriptionPosteFrancais.value}</li></p>`
  } catch (error) {
    return ``;
  }
}

const libelle = function (hit) {
  try {
    return `<h1>${hit.corps.libelleCourt} − ${hit.etablissementLibelle}</h1>`
  } catch (error) {
    return ``;
  }
}

const dates = function (hit) {
  try {
    const ouverture = moment.utc(hit.dateOuvertureCandidatures);
    const fermeture = moment.utc(hit.dateFermetureCandidatures);
    return `<p><span class="violet-text">${ouverture.format("L")} → ${fermeture.format("L")}</span></p>`;
  } catch (error) {
    return ``;
  }
}
search.addWidgets([
  stats({
    container: '#stats',
    templates: {
      text: `
      <p>
        {{#hasNoResults}}Pas de résultats{{/hasNoResults}}
        {{#hasOneResult}}1 résultat{{/hasOneResult}}
        {{#hasManyResults}}{{#helpers.formatNumber}}{{nbHits}}{{/helpers.formatNumber}} résultats{{/hasManyResults}}
      </p>
      `,
    },
  }),
  panel({
    templates: { header: 'Recherche Globale' },
  })(searchBox)({
    container: '#searchbox',
  }),
  panel({
    templates: { header: 'Postes par page' },
  })(hitsPerPage)({
    container: '#hits-per-page',
    items: [
      { label: '10 postes par page', value: 10, default: true },
      { label: '25 postes par page', value: 25 },
      { label: '50 postes par page', value: 50 },
      { label: '100 postes par page', value: 100 },
    ],
  }),
  hits({
    container: '#hits',
    templates: {
      empty: '',
      item: (hit, { html, components }) => {
        const isAbsolute = hit.editionPoste.startsWith('http://') || hit.editionPoste.startsWith('https://');
        const link = isAbsolute ? hit.editionPoste : `/${hit.editionPoste}`;
        return `
        <article>
          ${libelle(hit)}
          ${dates(hit)}
          ${localisation(hit)}
          ${typeCandidatures(hit)}
          ${domaines(hit)}
          ${sections(hit)}
          ${laboratoires(hit)}
          ${description(hit)}
          <h4>Contenu de la fiche de poste :</h4>
          <p>${(hit._snippetResult.extracted_text.value)}</p>
          <a class="button" href="${link}">Fiche de poste</a>
          ${contacts(hit)}
        </article>
      `;
      },
    },
  }),
  configure({
    hitsPerPage: 10,
    attributesToSnippet: ['extracted_text:50'],
    snippetEllipsisText: '...',
  }),
  panel({
    templates: { header: 'Sections' },
  })(refinementList)({
    container: '#sections-list',
    attribute: 'sections.libelle',
    sortBy: ['isRefined', 'name:asc'],
    limit: 100,
    searchable: true
  }),
  panel({
    templates: { header: 'Corps' },
  })(refinementList)({
    container: '#corps-list',
    attribute: 'corps.libelle',
    sortBy: ['isRefined', 'name:asc'],
    limit: 100,
  }),
  panel({
    templates: { header: 'Régions' },
  })(refinementList)({
    container: '#regions-list',
    attribute: 'region.libelle',
    sortBy: ['isRefined', 'name:asc'],
    limit: 100,
    searchable: true
  }),
  panel({
    templates: { header: 'Domaines' },
  })(refinementList)({
    container: '#domaines-list',
    attribute: 'domaineEuraxessComplets.libelle',
    sortBy: ['name:asc'],
    limit: 100,
    searchable: true
  }),
  pagination({
    container: '#pagination',
  }),
]);

search.start();
